// hooks/useLoadingCoordinator.js - Enhanced fixed version
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';

export const useLoadingCoordinator = (
  isSmartLoading,
  loadingStage,
  isDeckLoading,
  isChartDataLoading,
  isDeviceIdsLoading,
  isBusinessLoading,
  isHealthScoreLoading,
  selectedStoreId,
  viewState,
  hasBusinesses = false
) => {
  const [shouldShowLoader, setShouldShowLoader] = useState(true);
  const [forceComplete, setForceComplete] = useState(false);
  const timeoutRef = useRef(null);
  const mountedRef = useRef(true);
  const lastLogRef = useRef(0);
  const emergencyTimeoutRef = useRef(null);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (emergencyTimeoutRef.current) {
        clearTimeout(emergencyTimeoutRef.current);
      }
    };
  }, []);

  // Emergency timeout - force completion after 5 seconds
  useEffect(() => {
    emergencyTimeoutRef.current = setTimeout(() => {
      if (mountedRef.current && shouldShowLoader) {
        console.group('🚨 EMERGENCY TIMEOUT - 5 seconds');
        console.log('Force completing due to timeout');
        console.log('Final state at timeout:', {
          isSmartLoading,
          loadingStage,
          isDeckLoading,
          isChartDataLoading,
          isDeviceIdsLoading,
          isBusinessLoading,
          isHealthScoreLoading,
          selectedStoreId,
          viewState
        });
        console.groupEnd();
        setForceComplete(true);
        setShouldShowLoader(false);
      }
    }, 5000);
    
    return () => {
      if (emergencyTimeoutRef.current) {
        clearTimeout(emergencyTimeoutRef.current);
      }
    };
  }, []); // Only run once on mount

  const resetLoadingState = useCallback(() => {
  if (emergencyTimeoutRef.current) {
    clearTimeout(emergencyTimeoutRef.current);
  }
  setForceComplete(false);
  setShouldShowLoader(true);
  
  // Restart emergency timeout
  emergencyTimeoutRef.current = setTimeout(() => {
    if (mountedRef.current && shouldShowLoader) {
      setForceComplete(true);
      setShouldShowLoader(false);
    }
  }, 5000);
}, [shouldShowLoader]);
  
  // Calculate if ALL required data is loaded with enhanced error handling
  const isAllDataLoaded = useMemo(() => {
    const now = Date.now();
    const shouldLog = now - lastLogRef.current > 3000; // Log every 3 seconds max
    
    if (shouldLog) {
      console.group('🧮 useLoadingCoordinator calculation');
      lastLogRef.current = now;
    }
    
    const loadingStates = {
      isSmartLoading,
      isDeckLoading,
      isChartDataLoading,
      isDeviceIdsLoading,
      isBusinessLoading,
      isHealthScoreLoading
    };
    
    const activeLoaders = Object.entries(loadingStates)
      .filter(([key, isLoading]) => isLoading)
      .map(([key]) => key);
    
    if (shouldLog) {
      console.log('📊 Loading states:', loadingStates);
      console.log('🔴 Active loaders:', activeLoaders);
      console.log('🎯 Context:', { 
        viewState, 
        selectedStoreId: selectedStoreId || 'none', 
        hasBusinesses,
        loadingStage
      });
    }
    
    // ENHANCED: Check for error states that should complete loading
    const hasErrorState = loadingStage === 'error';
    const hasSelectedStore = !!selectedStoreId;
    const hasRequiredBusinessData = hasBusinesses && !isBusinessLoading;
    
    // If we're in an error state but have basic data, we should complete
    if (hasErrorState && hasSelectedStore && hasRequiredBusinessData) {
      if (shouldLog) {
        console.log('✅ Error state detected but basic data available - completing!');
        console.groupEnd();
      }
      return true;
    }
    
    // If nothing is actively loading, we're done
    const nothingLoading = activeLoaders.length === 0;
    if (nothingLoading) {
      if (shouldLog) {
        console.log('✅ Nothing is loading - should complete!');
        console.groupEnd();
      }
      return true;
    }
    
    // If only smart loading is active and we have essential data
    if (activeLoaders.length === 1 && activeLoaders[0] === 'isSmartLoading') {
      const essentialDataLoaded = !isDeckLoading && !isChartDataLoading && hasSelectedStore;
      if (essentialDataLoaded) {
        if (shouldLog) {
          console.log('✅ Smart loading stuck but essential data ready - completing!');
          console.groupEnd();
        }
        return true;
      }
    }
    
    // If we have selected store and deck/chart data, complete regardless of other states
    if (hasSelectedStore && !isDeckLoading && !isChartDataLoading && hasRequiredBusinessData) {
      if (shouldLog) {
        console.log('✅ Essential data loaded - completing!');
        console.groupEnd();
      }
      return true;
    }
    
    if (shouldLog) {
      console.log('🔄 Still loading:', activeLoaders);
      console.groupEnd();
    }
    
    return false;
  }, [
    isSmartLoading,
    loadingStage,
    isDeckLoading, 
    isChartDataLoading, 
    isDeviceIdsLoading, 
    isBusinessLoading, 
    isHealthScoreLoading, 
    selectedStoreId, 
    viewState,
    hasBusinesses
  ]);
  
  // Hide loader when conditions are met
  useEffect(() => {
    if (forceComplete) return; // Don't process if already force completed
    
    console.log('🎬 Loader check:', { 
      isSmartLoading, 
      isAllDataLoaded, 
      shouldShowLoader,
      loadingStage,
      willHide: !isSmartLoading && isAllDataLoaded
    });
    
    // Enhanced completion conditions
    const shouldComplete = isAllDataLoaded;
    const hasEssentialData = !!selectedStoreId && !isDeckLoading && !isChartDataLoading;
    const isStuckInError = loadingStage === 'error' && hasEssentialData;
    
    if (shouldComplete || isStuckInError) {
      console.log('🎯 Ready to hide loader - conditions met', {
        shouldComplete,
        isStuckInError,
        loadingStage
      });
      
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      // Very short delay for smooth UX
      timeoutRef.current = setTimeout(() => {
        if (mountedRef.current && !forceComplete) {
          console.log('✅ Hiding loader now');
          setShouldShowLoader(false);
        }
      }, 100);
      
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }
  }, [isSmartLoading, isAllDataLoaded, loadingStage, selectedStoreId, isDeckLoading, isChartDataLoading, forceComplete]);
  
  return {
    shouldShowLoader: shouldShowLoader && !forceComplete,
    isAllDataLoaded,
    forceComplete,
    resetLoadingState,
    dataProgress: {
      hasSelectedStore: !!selectedStoreId,
      deckReady: !isDeckLoading,
      chartReady: !isChartDataLoading,
      storesReady: !isDeviceIdsLoading,
      businessReady: !isBusinessLoading,
      healthReady: !isHealthScoreLoading || viewState === 'business',
      hasBusinesses,
      loadingStage,
      isInErrorState: loadingStage === 'error'
    }
  };
};